import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AllCommunityModule, ModuleRegistry, DateFilterModule, } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { toast, ToastContainer } from 'react-toastify';
import axiosInstance from '../../../../lib/axios';
import { Box, Typography, CircularProgress, Tooltip, IconButton, Button } from '@mui/material';
import { usePermissions } from '../../../../GlobalFunction/userPermissions';
import { useSelector } from 'react-redux';
import { selectGlobalProperty } from '../../../../redux/propertySlice';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import '../../../../AgGridStyle.css';
import HomeIcon from '@mui/icons-material/Home';
import TicketDetailsPopup from './TktDetailsPopup';

ModuleRegistry.registerModules([AllCommunityModule, DateFilterModule,]);

//STYLE BLOCK *inside* the component (after the imports)

const paginationMobileStyles = `
  @media (max-width: 600px) {
    /* Parent container: column layout, center all items */
    .ag-theme-alpine .ag-paging-panel {
      display: flex !important;
      flex-direction: column !important;
      align-items: center !important;      /* center children horizontally */
      gap: 12px !important;
      padding: 12px !important;
      min-height: 100px;
      box-sizing: border-box;
      text-align: center;
    }

    /* Wrapper flex container for Total Records + Page Size */
    .ag-theme-alpine .ag-paging-row-summary-panel,
    .ag-theme-alpine .ag-paging-page-size {
      display: inline-flex !important;
      align-items: center;
      gap: 16px;
      font-size: 14px;
      white-space: nowrap;               /* prevent wrapping */
      justify-content: center;
      margin: 0 !important;
    }

    /* Put Total Records and Page Size side-by-side in one row */
    .ag-theme-alpine .ag-paging-panel > .ag-paging-row-summary-panel,
    .ag-theme-alpine .ag-paging-panel > .ag-paging-page-size {
      flex: none;
    }

    /* Buttons below, centered */
    .ag-theme-alpine .ag-paging-button-panel {
      width: 100%;
      display: flex !important;
      justify-content: center !important;
      flex-wrap: wrap;
      margin-top: 8px;
    }

    /* Page-size dropdown/input */
    .ag-theme-alpine .ag-paging-page-size select,
    .ag-theme-alpine .ag-paging-page-size input {
      max-width: 70px !important;
      font-size: 14px !important;
      padding: 4px;
    }
  }
`;

const RaiseServTktTable = () => {
  const [rowData, setRowData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const UserId = sessionStorage?.getItem('UserId');
  // const permissions = usePermissions("Raise Service Ticket Master");
  // const { AddRec = "0" } = permissions || {};
  const gridRef = useRef();
  const navigate = useNavigate();
  const globalProperty = useSelector(selectGlobalProperty);

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = paginationMobileStyles;
    document.head.appendChild(style);

    return () => document.head.removeChild(style);
  }, []);

  const colDefs = useMemo(() => [
    { field: 'RowNum', headerName: 'Sr.No', width: 70 },
    { field: 'TicketNo', headerName: 'Ticket No', width: 90 },

    // TICKET DATE – TEXT SEARCH ONLY (NO CALENDAR, NO dd/mm/yyyy PLACEHOLDER)
    {
      field: 'TktDate',
      headerName: 'Ticket Date',
      width: 130,
      filter: 'agTextColumnFilter', // Plain text filter
      filterParams: {
        filterOptions: ['contains', 'equals'],
        debounceMs: 300,
      },
      valueFormatter: (params) => {
        if (!params.value) return '';
        const d = new Date(params.value);
        return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
      },
    },

    // TICKET TIME – NEW COLUMN
    {
      field: 'TktDate',
      headerName: 'Start Time',
      width: 110,
      valueGetter: (params) => {
        if (!params.data?.TktDate) return '';
        const d = new Date(params.data.TktDate);
        return d.toLocaleTimeString('en-GB', {
          hour12: true,
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        });
      },
      cellStyle: { fontFamily: 'monospace' },
    },

    // REMAINING COLUMNS (UNCHANGED)
    { field: 'PropName', headerName: 'Property', width: 200 },
    { field: 'ServiceName', headerName: 'Service/Complain', width: 180 },
    { field: 'MobileNo', headerName: 'Mobile', width: 100 },
    { field: 'Name', headerName: 'RaisedBy', width: 100 },
    { field: 'CategoryName', headerName: 'Category', width: 130 },
    { field: 'SubCategoryName', headerName: 'SubCategory', width: 140 },
    { field: 'TotalCost', headerName: 'MatCost', width: 140 },
    { field: 'LeadTime', headerName: 'LeadTime', width: 140 },
    { field: 'DelayTime', headerName: 'DelayTime', width: 140 },
    { field: 'EndTime', headerName: 'EndTime', width: 140 },
    {
      field: 'TicketSt',
      headerName: 'Ticket Status',
      width: 100,
      cellRenderer: (params) => {
        let statusText = '';
        let statusColor = 'black';
        switch (params.value) {
          case 'O':
            statusText = 'Open';
            statusColor = 'green';
            break;
          case 'I':
            statusText = 'In Progress';
            statusColor = '#d47b0dff';
            break;
          case 'C':
            statusText = 'Closed';
            statusColor = '#8B0000';
            break;
          case 'R':
            statusText = 'Resolved';
            statusColor = '#008080';
            break;
          case 'H':
            statusText = 'Hold';
            statusColor = '#800080';
            break;
          default:
            statusText = 'Unknown';
            statusColor = 'gray';
            break;
        }
        return (
          <span style={{ color: statusColor, fontWeight: 'bold' }}>
            {statusText}
          </span>
        );
      },
    },
    // Smart Location Column (Replaces 4 columns)
    // {
    //   headerName: 'Location',
    //   width: 180,
    //   valueGetter: (params) => {
    //     const d = params.data;
    //     if (d.RoomBedName) return `Bed: ${d.RoomBedName}`;
    //     if (d.AmenityName) return `Amenity: ${d.AmenityName}`;
    //     if (d.CommonAreaName) return `Common Area: ${d.CommonAreaName}`;
    //     if (d.FloorName) return `Floor: ${d.FloorName}`;
    //     return '-';
    //   },
    //   cellStyle: { fontWeight: 500,  },
    //   tooltipValueGetter: (params) => params.value,
    // },
    { field: 'RoomBedName', headerName: 'Bed', width: 80 },
    { field: 'FloorName', headerName: 'Floor', width: 80 },
    { field: 'CommonAreaName', headerName: 'Common Area', width: 120 },
    { field: 'AmenityName', headerName: 'Amenity', width: 110 },
    { field: 'Description', headerName: 'Description', width: 110 },
    { field: 'ResRemark', headerName: 'Resolve Remark', width: 110 },
    { field: 'Remark', headerName: 'Remark', width: 110 },
  ], []);


  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    floatingFilter: true,
    width: 100,
    tooltipValueGetter: (params) => params.value ?? '',
  }), []);

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const response = await axiosInstance.post(
        "TrnTicketing/GetTrnTicketDashBoard",
        {
          start: 0,
          PageSize: 0,
          SearchText: "",
        }
      );

      const { data: { Status, Data } } = response;

      if (Status === 0 && Array.isArray(Data)) {
        setRowData(Data);
      }
    } catch (error) {
      toast.error("Error fetching tickets");
    }
  };

  const onGridReady = useCallback((params) => {
    // No sizeColumnsToFit here to preserve flex sizing
  }, []);

  // const handleRowDoubleClick = useCallback((params) => {
  //   navigate(`/raise-service-tkt?mode=view&raiseId=${params.data.raiseId}`);
  // }, [navigate]);

  const handleRowDoubleClick = useCallback((params) => {
    setSelectedTicket(params.data);
    setPopupOpen(true);
  }, []);
  
  const handleRowClick = useCallback((params) => {
    setSelectedTicket(params.data);
    setPopupOpen(true);
  }, []);

  const handlePermittedClick = () => {
    // if (AddRec === "1") {
    navigate('/raise-service-tkt');
    // }
  };

  const handleClearFilters = () => {
    gridRef.current?.api?.setFilterModel(null);
  };

  return (
    <Box
      sx={{
        width: { xs: "calc(100vw - 60px)", sm: "auto", md: "auto" },
        height: '90vh',
        padding: '10px',
        backgroundColor: 'rgb(249, 249, 249)',
        position: 'relative'
      }}
    >
      <ToastContainer />
      <Box
        sx={{
          backgroundColor: "#ffffff",
          padding: { xs: "8px 16px", sm: "12px 16px" },
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
          borderRadius: "12px",
          margin: "10px auto",
          maxWidth: "95%",
          flexDirection: { xs: "column", sm: "column", md: "row" },
          gap: 2,
        }}
      >
        <Breadcrumbs
          aria-label="breadcrumb"
          separator="›"
          sx={{
            fontSize: { xs: "14px", sm: "16px" },
            display: "flex",
            flexDirection: { xs: "column", sm: "column", md: "row" }
          }}
        >
          <Link
            underline="hover"
            color="#635bff"
            sx={{ cursor: "pointer", fontWeight: "500", display: "flex", alignItems: "center" }}
            onClick={() => navigate('/dashboard')}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            <Box component="span">Home</Box>
          </Link>
          <Link
            underline="hover"
            color="#635bff"
            sx={{ cursor: "pointer", fontWeight: "500" }}
            onClick={() => navigate('/service&ticket/complaint')}
          >
            Raise Ticket
          </Link>

          <Typography color="text.primary" sx={{ fontWeight: "500" }}>
            Raise Ticket Master
          </Typography>
        </Breadcrumbs>

        {/* Buttons container */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mt: { xs: 1, sm: 1, md: 0 },
            flexShrink: 0,
          }}
        >
          <Tooltip title='Clear all applied filter' arrow>
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              size="small"
              sx={{
                borderRadius: "50px",
                textTransform: "none",
                borderColor: "#635bff",
                color: "#635bff",
                "&:hover": {
                  borderColor: "#524ccc",
                  backgroundColor: "rgba(99, 91, 255, 0.04)",
                }
              }}
            >
              Clear Filters
            </Button>
          </Tooltip>

          <Tooltip
            placement="top"
            arrow
          >
            <span>
              <Button
                variant="contained"
                onClick={handlePermittedClick}
                size="small"
                sx={{
                  borderRadius: "50px",
                  padding: "6px 15px",
                  background: "#635bff",
                  "&:hover": {
                    background: "#524ccc"
                  }
                }}
              >
                <AddIcon fontSize="small" sx={{ mr: 0.5 }} /> New
              </Button>
            </span>
          </Tooltip>

          <Tooltip title='Previous Page' arrow>
            <Box sx={{
              display: "flex", alignItems: "center", gap: 1,
              justifyContent: "flex-start"
            }}
            >
              <IconButton
                onClick={() => navigate('/service&ticket/complaint')}
                sx={{
                  backgroundColor: '#635bff',
                  borderRadius: '50%',
                  width: 35,
                  height: 35,
                  '&:hover': {
                    backgroundColor: '#635bff',
                  },
                  boxShadow: 3,
                }}
              >
                <ArrowBackIcon sx={{ color: 'white' }} />
              </IconButton>
            </Box>
          </Tooltip>
        </Box>
      </Box>

      <Box className="table-grid ag-theme-alpine"
        sx={{ height: 'calc(97% - 56px)', width: '95%', margin: '0 auto', position: 'relative' }}
      >
        {isLoading ? (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10,
              textAlign: 'center',
            }}
          >
            <CircularProgress size="3rem" sx={{ color: '#635bff' }} />

            <Typography sx={{ marginTop: 2, color: '#635bff', fontWeight: 'bold' }}>
              Loading data...
            </Typography>
          </Box>
        ) : (
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={colDefs}
            defaultColDef={defaultColDef}
            pagination={true}
            paginationPageSize={1000}
            onRowDoubleClicked={handleRowDoubleClick}
            onRowClicked={handleRowClick}
            paginationPageSizeSelector={[100, 500, 1000, 5000]}
            onGridReady={onGridReady}
            enableCellTextSelection={true}
            rowHeight={25}
            headerHeight={30}
            floatingFiltersHeight={28}
            overlayNoRowsTemplate={'<span class="ag-overlay-loading-center">No rows to show</span>'}
            tooltipShowDelay={0}
            getRowStyle={(params) => ({
              backgroundColor: params.node.rowIndex % 2 === 0 ? '#f1f5fd' : '#ffffff'
            })}
          />
        )}
      </Box>
      <Box sx={{
        position: 'absolute',
        bottom: 16,
        left: 70,
        fontSize: '14px',
        color: 'black',
        display: { xs: 'none', sm: 'none', md: 'block' },
      }}>
        Total Records: <b>{rowData.length}</b>
      </Box>
      {/* ← ADD THIS POPUP */}
      <TicketDetailsPopup
        open={popupOpen}
        onClose={() => setPopupOpen(false)}
        ticket={selectedTicket}
      />
    </Box>
  );
};

export default RaiseServTktTable;